import { UseFormSetValue, UseFormWatch } from 'react-hook-form'
import React, { useState } from 'react'
import { t } from 'ttag'
import { FormData } from '../../ChallengeAdmin'
import { CustomTypeCategory } from '../../../../services/CustomTypes'
import { IconButton } from '@material-ui/core'

import { ReactComponent as MoreIcon } from '../../../../assets/v3/icons/more.svg'
import { isAutomatedType, isSimpleDayType } from '../../../../common/helpers/challenges'
import { darkBlue } from '../../../../styles/material-ui-theme-v2'
import YumMenu, { YumMenuItem } from '../../../../components/YumMenu/YumMenu'
import { hasFeatureFlag } from '../../../../common/helpers/featureFlags'
import { UserCompanyDto } from '../../../../services/api.types'
import { totalMaxLabel } from '../../../ChallengeInfo/ChallengeInfo'

interface ChallengePointsMoreFeaturesProps {
  setValue: UseFormSetValue<FormData>
  watch: UseFormWatch<FormData>
  name: any // The field name for simpleManualEntry (using any to match existing pattern)
  dailyGoalName: any // The field name for dailyGoal (using any to match existing pattern)
  dailyGoalEnabledName: any // The field name for dailyGoalEnabled
  totalMaxEnabledName: any // The field name for totalMaxEnabled
  item: any
  index: number // Add index to properly access totalMax field
  userCompanies: UserCompanyDto[] // <-- add this
}

export default function ChallengePointsMoreFeatures(props: ChallengePointsMoreFeaturesProps) {
  const {
    setValue,
    watch,
    name,
    dailyGoalName,
    dailyGoalEnabledName,
    totalMaxEnabledName,
    item,
    index,
    userCompanies,
  } = props

  // State for menu anchor element
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = Boolean(menuAnchorEl)

  // Track if user has manually disabled the fields
  const [userDisabledDailyMax, setUserDisabledDailyMax] = useState(false)
  const [userDisabledTotalMax, setUserDisabledTotalMax] = useState(false)

  // Watch the values
  const simpleManualEntry = watch(name)
  const dailyGoalEnabled = watch(dailyGoalEnabledName)
  const totalMaxEnabled = watch(totalMaxEnabledName)
  const manualEntryEnabled = watch('manualEntryEnabled')
  const dailyGoalValue = watch(dailyGoalName)
  const totalMaxValue = watch(`challengePoints.${index}.totalMax`)

  // Check if daily max or total max have values and enable them by default
  React.useEffect(() => {
    if (dailyGoalEnabled === undefined && dailyGoalValue && dailyGoalValue > 0) {
      setValue(dailyGoalEnabledName, true)
    }
  }, [dailyGoalEnabled, dailyGoalValue, dailyGoalEnabledName, setValue])

  React.useEffect(() => {
    if (totalMaxEnabled === undefined && totalMaxValue && totalMaxValue > 0) {
      setValue(totalMaxEnabledName, true)
    }
  }, [totalMaxEnabled, totalMaxValue, totalMaxEnabledName, setValue])

  // Handle menu open and close
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  // Handle simple manual entry toggle
  const handleSimpleManualEntryToggle = () => {
    const newValue = !simpleManualEntry
    setValue(name, newValue)

    // Auto-enable daily max when one-click manual entry is enabled
    if (newValue && !dailyGoalEnabled) {
      setValue(dailyGoalEnabledName, true)
      setUserDisabledDailyMax(false)
    }

    handleMenuClose()
  }

  // Handle toggle change (when user clicks the toggle switch)
  const handleToggleChange = (checked: boolean) => {
    setValue(name, checked)

    // Auto-enable daily max when one-click manual entry is enabled
    if (checked && !dailyGoalEnabled) {
      setValue(dailyGoalEnabledName, true)
      setUserDisabledDailyMax(false)
    }
  }

  // Handle daily max visibility toggle
  const handleDailyMaxToggle = (checked: boolean) => {
    setValue(dailyGoalEnabledName, checked)
    if (!checked) {
      setUserDisabledDailyMax(true)
      // Auto-disable one-click manual entry when daily max is disabled
      if (simpleManualEntry) {
        setValue(name, false)
      }
    } else {
      setUserDisabledDailyMax(false)
    }
  }

  // Handle total max visibility toggle
  const handleTotalMaxToggle = (checked: boolean) => {
    setValue(totalMaxEnabledName, checked)
    if (!checked) {
      setUserDisabledTotalMax(true)
    } else {
      setUserDisabledTotalMax(false)
    }
  }

  const showSimpleManualEntry =
    ![CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.BONUS_SIMPLE, CustomTypeCategory.BONUS_WITH_AMOUNT].includes(
      item.category,
    ) &&
    !isSimpleDayType(item.category) &&
    (manualEntryEnabled || !isAutomatedType(item.unit))

  // Check if daily max should be shown based on activity category
  const showDailyMax =
    ![CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.BONUS_SIMPLE, CustomTypeCategory.BONUS_WITH_AMOUNT].includes(
      item.category,
    ) && !isSimpleDayType(item.category)

  // Create menu items - start with one-click manual entry if it should be shown
  const menuItems: YumMenuItem[] = []

  // Add one-click manual entry first if it should be shown
  if (showSimpleManualEntry) {
    menuItems.push({
      id: 'simple-manual-entry',
      label: t`One-click manual entry`,
      //   icon: simpleManualEntry ? (
      //     <SimpleManualEntryActiveIcon style={{ width: '16px', height: '16px' }} />
      //   ) : (
      //     <SimpleManualEntryInactiveIcon style={{ width: '16px', height: '16px' }} />
      //   ),
      onClick: handleSimpleManualEntryToggle,
      toggle: true, // Show toggle switch
      toggleValue: simpleManualEntry, // Current toggle state
      onToggleChange: handleToggleChange, // Handle toggle changes
    })
  }

  // Add daily max only if it should be shown
  if (showDailyMax) {
    menuItems.push({
      id: 'daily-max',
      label: t`Daily limit per user`,
      onClick: () => {
        const newValue = !dailyGoalEnabled
        setValue(dailyGoalEnabledName, newValue)
        if (!newValue) {
          setUserDisabledDailyMax(true)
          // Auto-disable one-click manual entry when daily max is disabled
          if (simpleManualEntry) {
            setValue(name, false)
          }
        } else {
          setUserDisabledDailyMax(false)
        }
        handleMenuClose()
      },
      toggle: true,
      toggleValue: dailyGoalEnabled,
      onToggleChange: handleDailyMaxToggle,
    })
  }

  // Only add total-max if feature flag is enabled
  if (hasFeatureFlag(userCompanies, 'totalMax')) {
    menuItems.push({
      id: 'total-max',
      label: totalMaxLabel,
      onClick: () => {
        const newValue = !totalMaxEnabled
        setValue(totalMaxEnabledName, newValue)
        if (!newValue) {
          setUserDisabledTotalMax(true)
        } else {
          setUserDisabledTotalMax(false)
        }
        handleMenuClose()
      },
      toggle: true,
      toggleValue: totalMaxEnabled,
      onToggleChange: handleTotalMaxToggle,
    })
  }

  // Don't render anything if there are no menu items
  if (menuItems.length === 0) {
    return (
      <div
        style={{
          flexShrink: 0,
          marginBottom: '10px',
          width: '48px',
        }}
      />
    )
  }

  return (
    <>
      <IconButton
        style={{
          flexShrink: 0,
          marginBottom: '10px',
          position: 'relative',
          color: darkBlue,
        }}
        title={t`More features`}
        aria-label={t`More features`}
        onClick={handleMenuClick}
        aria-controls={openMenu ? 'challenge-points-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openMenu ? 'true' : undefined}
      >
        <MoreIcon
          style={{
            padding: 2,
            width: 24,
            height: 24,
          }}
        />
      </IconButton>
      <YumMenu
        id="challenge-points-menu"
        anchorEl={menuAnchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        items={menuItems}
        ariaLabelledBy="challenge-points-more-button"
      />
    </>
  )
}
